"use client";

import { Button } from "@pex/ui-web";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

// Alt text from Webflow HTML source.
const LEFT_IMAGE = {
  src: "/images/recovery-groups/GroupsOverview_LeftImage_01.webp",
  alt: "A large group sits in a circle in an auditorium.",
};

const CENTER_IMAGE = {
  src: "/images/recovery-groups/GroupsOverview_Mobile_LeftImage_01.webp",
  alt: "A man with glasses and a mohawk smiles while seated.",
};

const RIGHT_IMAGE = {
  src: "/images/recovery-groups/GroupsOverview_RightImage_01_V2.webp",
  alt: "A group of people sits in chairs on the grass.",
};

export function RecoveryGroupsHero() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Global scrollY — y=0 at page load, no pre-translation on the initial stagger.
  const { scrollY } = useScroll();

  // Left drifts DOWN; centre moves UP slightly; right moves UP moderately.
  // Desktop values derived from Webflow reference gap measurements:
  //   centre–left bottom gap:  60px at start → 128px (left now lowest) at end
  //     ∴ left_down + centre_up = 60 + 128 = 188  →  left=60, centre=128
  //   centre–right bottom gap: 228px at start → 275px at end
  //     ∴ right_up − centre_up = 47             →  right=175
  const leftY = useTransform(scrollY, [0, 700], [0, isDesktop ? 60 : 18]);
  const centerY = useTransform(scrollY, [0, 700], [0, isDesktop ? -128 : -37]);
  const rightY = useTransform(scrollY, [0, 700], [0, isDesktop ? -175 : -51]);

  return (
    <section className="overflow-hidden py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Text block */}
        <h1 className="heading-1 max-w-3xl font-bold">
          There&apos;s No Place Like{" "}
          <span className="font-serif italic">Hope</span>
        </h1>

        <p className="body-lg mt-6 max-w-2xl text-muted-foreground">
          Recovery Groups are weekly meetings for individuals and families
          impacted by addiction. Providing hope, connection, accountability, and
          empowerment, groups promote sustainable recovery through long-term
          support.
        </p>

        <div className="mt-8 flex w-full flex-col items-start gap-3 sm:w-auto sm:flex-row md:gap-4">
          <Button
            asChild
            className="w-full sm:w-auto"
            size="lg"
            variant="fancy"
          >
            <a
              href={`${process.env.NEXT_PUBLIC_FINDER_URL}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Join a Group
            </a>
          </Button>
          <Button
            asChild
            className="w-full sm:w-auto"
            size="lg"
            variant="secondary-fancy"
          >
            <Link href="/partner/overview">Start a Group</Link>
          </Button>
        </div>

        {/*
          Overlapping collage gallery — structure mirrors Webflow HTML.
          Container: aspect-[5/3] (1280×768 at max-w-7xl), all children absolute.

          Z-order (front→back): Right (z-30) › Centre (z-20) › Left (z-10).
          This matches Webflow DOM order: left first, centre second, right last
          (later elements sit on top by default; explicit z-index makes it clear).

          Initial vertical positions at 768px container height:
            Left   top=12% → bottom ≈ 604px
            Centre top=3%  → bottom ≈ 663px  (centre 59px below left ≈ 60px ref)
            Right  top=6%  → bottom ≈ 430px  (centre 233px below right ≈ 228px ref)

          Horizontal overlap: left (0–40%) and right (60–100%) each overlap
          centre (25–75%) by ~15% of container width on each side.
        */}
        <div className="relative mt-20 aspect-[5/3] w-full sm:mt-28">
          {/* Left — square, top-left, most behind */}
          <motion.div
            className="absolute top-[12%] left-0 z-10 aspect-[1/1] w-[40%] overflow-hidden rounded-lg md:rounded-2xl"
            style={{ y: leftY, willChange: "transform" }}
          >
            <Image
              alt={LEFT_IMAGE.alt}
              className="object-cover"
              fill
              priority
              sizes="(max-width: 640px) 40vw, (max-width: 1280px) 30vw, 512px"
              src={LEFT_IMAGE.src}
            />
          </motion.div>

          {/* Centre — square, 50% wide, centred horizontally, middle layer */}
          <motion.div
            className="absolute top-[3%] left-1/4 z-20 aspect-[1/1] w-1/2 overflow-hidden rounded-lg md:rounded-2xl"
            style={{ y: centerY, willChange: "transform" }}
          >
            <Image
              alt={CENTER_IMAGE.alt}
              className="object-cover"
              fill
              priority
              sizes="(max-width: 640px) 50vw, (max-width: 1280px) 40vw, 640px"
              src={CENTER_IMAGE.src}
            />
          </motion.div>

          {/* Right — landscape (4:3), right edge, on top */}
          <motion.div
            className="absolute top-[6%] right-0 z-30 aspect-[4/3] w-[40%] overflow-hidden rounded-lg md:rounded-2xl"
            style={{ y: rightY, willChange: "transform" }}
          >
            <Image
              alt={RIGHT_IMAGE.alt}
              className="object-cover"
              fill
              priority
              sizes="(max-width: 640px) 40vw, (max-width: 1280px) 30vw, 512px"
              src={RIGHT_IMAGE.src}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
